/**
 * GL-DESIGN-V3-2026Q4 — State Compliance Matrix page.
 *
 * Lists all states with mini "coverage" chip (rules authored / total topics).
 * Click a state → see its rules grouped by category, each expandable to edit.
 * Rules with inherits_federal=true show a navy "FEDERAL" chip + fallback summary.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import {
  Card, PrimaryPill, OutlinePill, StatusPill, Segmented,
  InsetInput, InsetTextArea, IconButton, Eyebrow, V3,
} from '@/components/v3/kit';
import { CaretDownIcon, CaretUpIcon, CheckCircleIcon, WarningCircleIcon, PlusIcon } from '@phosphor-icons/react';

const STATUS_META = {
  draft: { tone: V3.muted, label: 'Draft' },
  needs_review: { tone: V3.p2, label: 'Needs review' },
  verified: { tone: V3.p3, label: 'Verified' },
  stale: { tone: V3.p2, label: 'Stale' },
  retired: { tone: V3.muted, label: 'Retired' },
};

const PLAN_META = {
  federal: { label: 'Federal OSHA', color: V3.muted },
  state_plan_full: { label: 'Full state plan', color: V3.navy },
  state_plan_public_only: { label: 'Public-sector plan', color: V3.p2 },
};

export default function MatrixPage() {
  const { stateCode } = useParams();
  const nav = useNavigate();
  const [states, setStates] = useState(null);
  const [topics, setTopics] = useState(null);
  const [activeStateCode, setActiveStateCode] = useState(stateCode?.toUpperCase() || 'NC');
  const [rules, setRules] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'authored' | 'missing'

  useEffect(() => {
    Promise.all([
      api.get('/matrix/states'),
      api.get('/matrix/topics'),
    ])
      .then(([s, t]) => { setStates(s.data); setTopics(t.data); })
      .catch((e) => { toast.error(formatApiError(e)); setStates([]); setTopics([]); });
  }, []);

  useEffect(() => {
    if (!activeStateCode) return;
    setRules(null);
    api.get(`/matrix/rules?state=${activeStateCode}`)
      .then((r) => setRules(r.data || []))
      .catch(() => setRules([]));
  }, [activeStateCode]);

  const activeState = useMemo(
    () => (states || []).find((s) => s.code === activeStateCode) || null,
    [states, activeStateCode]
  );

  const rulesByTopic = useMemo(() => {
    const map = {};
    (rules || []).forEach((r) => { map[r.topic_id] = r; });
    return map;
  }, [rules]);

  const topicRows = useMemo(() => {
    if (!topics) return [];
    const rows = topics.map((t) => ({ topic: t, rule: rulesByTopic[t.id] || null }));
    if (filter === 'authored') return rows.filter((r) => r.rule);
    if (filter === 'missing') return rows.filter((r) => !r.rule);
    return rows;
  }, [topics, rulesByTopic, filter]);

  const coverage = useMemo(() => {
    if (!topics || !rules) return { authored: 0, total: 0, atlasReady: 0 };
    const authored = rules.length;
    const atlas = rules.filter((r) => r.atlas_ready).length;
    return { authored, total: topics.length, atlasReady: atlas };
  }, [topics, rules]);

  function handleStateChange(code) {
    setActiveStateCode(code);
    nav(`/matrix/${code}`, { replace: true });
    setExpandedId(null);
  }

  const loading = states === null || topics === null;

  return (
    <div style={{ padding: '28px 20px 60px' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        {/* ---------- Header ---------- */}
        <Eyebrow color={V3.muted}>State Compliance Matrix</Eyebrow>
        <h1 style={{ fontSize: 'clamp(26px, 3vw, 32px)', fontWeight: 700, color: V3.navy, letterSpacing: '-0.025em', marginTop: 4 }}>
          Reference by state
        </h1>
        <p style={{ fontSize: 14, color: V3.muted, marginTop: 6, maxWidth: 640, lineHeight: 1.55 }}>
          Federal OSHA baseline plus 22 state-plan variations. ATLAS and the drafting engine read from this matrix when generating state-specific citations.
        </p>

        {loading ? (
          <Card padding={28} style={{ marginTop: 20 }}>
            <div style={{ color: V3.muted }}>Loading matrix…</div>
          </Card>
        ) : (
          <div className="grid gap-5 mt-6" style={{ gridTemplateColumns: 'minmax(240px, 1fr) 2.4fr' }}>
            {/* ---------- Left: state picker ---------- */}
            <Card padding={14}>
              <Eyebrow color={V3.muted}>States ({(states || []).length})</Eyebrow>
              <div style={{ marginTop: 10, maxHeight: '70vh', overflow: 'auto', paddingRight: 4 }}>
                {(states || []).map((s) => {
                  const isActive = s.code === activeStateCode;
                  const planMeta = PLAN_META[s.plan_type] || PLAN_META.federal;
                  return (
                    <button
                      key={s.code}
                      type="button"
                      onClick={() => handleStateChange(s.code)}
                      data-testid={`matrix-state-${s.code}`}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '10px 14px', marginBottom: 6, borderRadius: 14,
                        background: isActive ? V3.navy : 'transparent',
                        color: isActive ? '#fff' : V3.ink,
                        boxShadow: isActive ? '0 8px 16px -8px rgba(20,38,84,.5)' : 'none',
                        border: 0, cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>
                          <span style={{ fontFamily: V3.mono, marginRight: 8, opacity: isActive ? 1 : 0.5 }}>{s.code}</span>
                          {s.name}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: isActive ? V3.gold : planMeta.color, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                        {planMeta.label}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* ---------- Right: state detail + rules ---------- */}
            <div>
              {activeState && (
                <Card padding={22} style={{ marginBottom: 18 }}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: V3.mono, fontSize: 13, fontWeight: 700, color: V3.gold, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{activeState.code}</span>
                        <h2 style={{ fontSize: 24, fontWeight: 700, color: V3.navy, letterSpacing: '-0.02em' }}>{activeState.name}</h2>
                        <StatusPill dot={PLAN_META[activeState.plan_type].color}>{PLAN_META[activeState.plan_type].label}</StatusPill>
                      </div>
                      {activeState.agency_name && (
                        <div style={{ fontSize: 13.5, color: V3.muted, marginTop: 8 }}>
                          {activeState.agency_name}
                          {activeState.agency_short && ` · ${activeState.agency_short}`}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 13 }}>
                        {activeState.reporting_phone && (
                          <span style={{ color: V3.ink }}>📞 {activeState.reporting_phone}</span>
                        )}
                        {activeState.reporting_phone_afterhours && (
                          <span style={{ color: V3.muted }}>After-hours: {activeState.reporting_phone_afterhours}</span>
                        )}
                        {activeState.reporting_online_url && (
                          <a href={activeState.reporting_online_url} target="_blank" rel="noreferrer" style={{ color: V3.navy, textDecoration: 'underline' }}>
                            Report online →
                          </a>
                        )}
                      </div>
                      {activeState.notes && (
                        <p style={{ fontSize: 13, color: V3.muted, marginTop: 10, lineHeight: 1.5 }}>{activeState.notes}</p>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontFamily: V3.mono, fontSize: 24, fontWeight: 700, color: V3.navy }}>
                          {coverage.authored}<span style={{ opacity: 0.4 }}>/{coverage.total}</span>
                        </div>
                        <div style={{ fontSize: 11, color: V3.muted, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>Topics authored</div>
                      </div>
                      {coverage.atlasReady > 0 && (
                        <StatusPill dot={V3.p3}>{coverage.atlasReady} ATLAS-ready</StatusPill>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Filter + Rules list */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 style={{ fontSize: 17, fontWeight: 700, color: V3.navy }}>Topics</h3>
                <Segmented
                  value={filter}
                  onChange={setFilter}
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Authored', value: 'authored' },
                    { label: 'Missing', value: 'missing' },
                  ]}
                />
              </div>

              {rules === null ? (
                <Card padding={28}><div style={{ color: V3.muted }}>Loading rules…</div></Card>
              ) : topicRows.length === 0 ? (
                <Card padding={28}>
                  <div style={{ color: V3.muted }}>No topics match this filter.</div>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {topicRows.map(({ topic, rule }) => (
                    <RuleRow
                      key={topic.id}
                      topic={topic}
                      rule={rule}
                      stateCode={activeStateCode}
                      expanded={expandedId === topic.id}
                      onToggle={() => setExpandedId((prev) => prev === topic.id ? null : topic.id)}
                      onCreated={(r) => setRules((prev) => [...(prev || []), r])}
                      onUpdated={(r) => setRules((prev) => (prev || []).map((x) => x.id === r.id ? r : x))}
                      onDeleted={(rid) => setRules((prev) => (prev || []).filter((x) => x.id !== rid))}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// RuleRow — expandable editor for a single (state, topic) rule
// ----------------------------------------------------------------
function RuleRow({ topic, rule, stateCode, expanded, onToggle, onCreated, onUpdated, onDeleted }) {
  const hasRule = !!rule;
  const meta = hasRule ? STATUS_META[rule.review_status] || STATUS_META.draft : null;

  return (
    <div
      style={{
        background: V3.surface,
        borderRadius: 20,
        boxShadow: V3.raised,
        padding: 16,
      }}
      data-testid={`matrix-rule-${topic.id}`}
    >
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <span
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: hasRule ? (rule.atlas_ready ? V3.p3 : V3.gold) : V3.tint,
              color: hasRule ? '#fff' : V3.muted,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {hasRule ? (rule.atlas_ready ? <CheckCircleIcon size={18} weight="fill" /> : <WarningCircleIcon size={18} weight="fill" />) : <PlusIcon size={16} weight="bold" />}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: V3.navy }}>{topic.name}</div>
            <div style={{ fontSize: 12, color: V3.muted, fontFamily: V3.mono, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {topic.category} · {topic.federal_citation}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {hasRule && meta && <StatusPill dot={meta.tone}>{meta.label}</StatusPill>}
          {hasRule && rule.inherits_federal && <StatusPill dot={V3.muted} bg="#fff">Federal</StatusPill>}
          {!hasRule && <StatusPill dot={V3.muted}>Not authored</StatusPill>}
          <IconButton icon={expanded ? 'caret-up' : 'caret-down'} size={34} tint />
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(20,38,84,0.08)' }}>
          <RuleEditor
            topic={topic}
            rule={rule}
            stateCode={stateCode}
            onCreated={onCreated}
            onUpdated={onUpdated}
            onDeleted={onDeleted}
            onDone={onToggle}
          />
        </div>
      )}
    </div>
  );
}

function RuleEditor({ topic, rule, stateCode, onCreated, onUpdated, onDeleted, onDone }) {
  const isNew = !rule;
  const [form, setForm] = useState(() => ({
    requirement_summary: rule?.requirement_summary || '',
    citation_primary: rule?.citation?.primary || '',
    citation_federal_analogue: rule?.citation?.federal_analogue || topic.federal_citation || '',
    hours_to_report: rule?.thresholds?.hours_to_report ?? '',
    penalty_max: rule?.penalty_range_usd?.max ?? '',
    action_when: rule?.action_required?.when || '',
    action_how: rule?.action_required?.how || '',
    consultant_notes: rule?.consultant_notes || '',
    review_status: rule?.review_status || 'draft',
    atlas_ready: rule?.atlas_ready || false,
    inherits_federal: rule?.inherits_federal || false,
    applies: rule?.applies ?? true,
  }));
  const [busy, setBusy] = useState(false);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  async function save() {
    setBusy(true);
    try {
      const payload = {
        requirement_summary: form.requirement_summary,
        citation: {
          primary: form.citation_primary,
          federal_analogue: form.citation_federal_analogue,
          supporting: [],
        },
        thresholds: {
          hours_to_report: form.hours_to_report ? parseInt(form.hours_to_report, 10) : null,
          trigger: '',
        },
        penalty_range_usd: {
          min: 0,
          max: form.penalty_max ? parseInt(form.penalty_max, 10) : null,
          unit: 'per_violation',
        },
        action_required: {
          who: 'employer',
          when: form.action_when,
          how: form.action_how,
        },
        consultant_notes: form.consultant_notes,
        review_status: form.review_status,
        atlas_ready: form.atlas_ready,
        inherits_federal: form.inherits_federal,
        applies: form.applies,
      };
      if (isNew) {
        const r = await api.post('/matrix/rules', { ...payload, state_code: stateCode, topic_id: topic.id });
        onCreated?.(r.data);
        toast.success('Rule created');
      } else {
        const r = await api.patch(`/matrix/rules/${rule.id}`, payload);
        onUpdated?.(r.data);
        toast.success('Rule updated');
      }
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!rule) return;
    if (!window.confirm(`Delete the ${stateCode} rule for "${topic.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/matrix/rules/${rule.id}`);
      onDeleted?.(rule.id);
      toast.success('Rule deleted');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
      <div style={{ gridColumn: '1 / -1' }}>
        <label style={labelStyle}>Requirement summary</label>
        <InsetTextArea
          value={form.requirement_summary}
          onChange={(v) => up('requirement_summary', v)}
          placeholder="One sentence describing what employers must do."
          rows={3}
          minHeight={96}
        />
      </div>
      <div>
        <label style={labelStyle}>Primary citation</label>
        <InsetInput value={form.citation_primary} onChange={(v) => up('citation_primary', v)} placeholder="13 NCAC 07A .0300" />
      </div>
      <div>
        <label style={labelStyle}>Federal analogue</label>
        <InsetInput value={form.citation_federal_analogue} onChange={(v) => up('citation_federal_analogue', v)} placeholder="29 CFR 1904.39" />
      </div>
      <div>
        <label style={labelStyle}>Hours to report</label>
        <InsetInput type="number" value={form.hours_to_report} onChange={(v) => up('hours_to_report', v)} placeholder="8" />
      </div>
      <div>
        <label style={labelStyle}>Max penalty (USD)</label>
        <InsetInput type="number" value={form.penalty_max} onChange={(v) => up('penalty_max', v)} placeholder="165514" />
      </div>
      <div>
        <label style={labelStyle}>Action — when</label>
        <InsetInput value={form.action_when} onChange={(v) => up('action_when', v)} placeholder="within 8 hours of knowledge" />
      </div>
      <div>
        <label style={labelStyle}>Action — how</label>
        <InsetInput value={form.action_how} onChange={(v) => up('action_how', v)} placeholder="phone 919-707-7900 or online form" />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <label style={labelStyle}>Consultant notes</label>
        <InsetTextArea
          value={form.consultant_notes}
          onChange={(v) => up('consultant_notes', v)}
          placeholder="Edge cases, common gaps, enforcement patterns."
          rows={2}
          minHeight={72}
        />
      </div>

      <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <Segmented
          size="sm"
          value={form.review_status}
          onChange={(v) => up('review_status', v)}
          options={[
            { label: 'Draft', value: 'draft' },
            { label: 'Needs review', value: 'needs_review' },
            { label: 'Verified', value: 'verified' },
          ]}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: V3.ink }}>
          <input type="checkbox" checked={form.atlas_ready} onChange={(e) => up('atlas_ready', e.target.checked)} />
          ATLAS-ready
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: V3.ink }}>
          <input type="checkbox" checked={form.inherits_federal} onChange={(e) => up('inherits_federal', e.target.checked)} />
          Inherits federal
        </label>
      </div>

      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
        <PrimaryPill icon={isNew ? 'plus' : 'floppy-disk'} onClick={save} testid="matrix-rule-save">
          {busy ? 'Saving…' : isNew ? 'Create rule' : 'Save changes'}
        </PrimaryPill>
        {!isNew && <OutlinePill icon="trash" onClick={remove}>Delete</OutlinePill>}
        <OutlinePill onClick={onDone}>Close</OutlinePill>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: V3.navy,
  marginBottom: 6,
  paddingLeft: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};
